// Nutzungssperre durch ein Startup-Gate (heute nur: Re-Akzept der Rechtstexte).
//
// Warum ein eigener Mini-Store: Der Server sperrt Konten mit ausstehender
// Zustimmung auf fast allen /api-Routen mit 409 `legal_acceptance_required`
// (server/legal-presentation.js createLegalUsageGuard). Der Content-Bootstrap
// der SyncEngine startet aber im SyncProvider — OBERHALB des
// StartupGateCoordinators, der den Gate-Zustand kennt. Ohne dieses Signal lief
// der Bootstrap trotz offenem Gate los, jede Anfrage endete mit 409, und das
// Einrichtungs-Vollbild (ContentSetupOverlay, z-index max) verdeckte den
// Zustimmungsdialog → frische Installationen hingen fest (19.10.2026).
//
// Der Koordinator meldet hier, fuer welchen Nutzer die Nutzung gerade gesperrt
// ist; SyncProvider/Engine und Overlay lesen es. Kein Publisher = nicht
// gesperrt (Tests, isolierte Seiten).

type Listener = () => void;

let blockedUserKey: string | null = null;
const listeners = new Set<Listener>();

export function setUsageBlocked(userKey: string | null, blocked: boolean): void {
  const next = blocked && userKey ? userKey : null;
  if (next === blockedUserKey) return;
  blockedUserKey = next;
  for (const listener of listeners) listener();
}

export function isUsageBlocked(userKey: string | number | null | undefined): boolean {
  if (userKey === null || userKey === undefined) return false;
  return blockedUserKey !== null && blockedUserKey === String(userKey);
}

/** Ist die Nutzung fuer den aktuell angemeldeten Nutzer gesperrt? Der
 * Koordinator meldet nur fuer seinen authKey und raeumt beim Wechsel. */
export function isAnyUsageBlocked(): boolean {
  return blockedUserKey !== null;
}

export function subscribeUsageGate(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Nur fuer Tests: Store zuruecksetzen. */
export function resetUsageGateForTests(): void {
  blockedUserKey = null;
}

// Server meldet „Zustimmung fehlt", obwohl der Client das Gate fuer erledigt
// haelt (z. B. neue Rechtstext-Version mitten in der Sitzung). Das Gate holt
// /legal-status dann neu. Event statt direkter Kopplung, damit api/client.ts
// nichts vom Gate wissen muss (wie sessionEvents.ts).
export const LEGAL_ACCEPTANCE_REQUIRED_EVENT = 'lorath:legal_acceptance_required';

export function isLegalAcceptanceRequiredError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as { status?: unknown; code?: unknown; error_code?: unknown };
  return candidate.status === 409
    && (candidate.code === 'legal_acceptance_required' || candidate.error_code === 'legal_acceptance_required');
}

export function emitLegalAcceptanceRequired(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(LEGAL_ACCEPTANCE_REQUIRED_EVENT));
}

export function onLegalAcceptanceRequired(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const listener = () => callback();
  window.addEventListener(LEGAL_ACCEPTANCE_REQUIRED_EVENT, listener);
  return () => window.removeEventListener(LEGAL_ACCEPTANCE_REQUIRED_EVENT, listener);
}
